import React, { useState } from "react";
import { MousePointerClick, Plus, X } from "lucide-react";
import { IconButton, InfoTip, JsonEditor, optionsTip, SelectInput, TextInput } from "../common";
import { explain, GLOSSARY } from "../glossary";

const TRANSFORMS_TIP = optionsTip(GLOSSARY.mapping.addTransform, GLOSSARY.transforms);
const REFS_TIP = optionsTip(GLOSSARY.mapping.modes.ref, GLOSSARY.mapping.refs);

// Optional arguments shown for transforms, besides the required ones from the server.
const OPTIONAL_ARGS = {
    to_number: ["locale"],
    to_integer: ["locale"],
    to_boolean: ["truthy"],
    to_iso_date: ["format"],
    round: ["places"],
    replace: ["with", "flags"],
    join: ["sep"],
    split: ["sep"],
    sort: ["dir"],
    truncate_list: ["max_chars", "sep"],
    map_values: ["default"],
    null_if_match: ["flags"],
};
const NUMERIC_ARGS = ["max", "n", "places", "max_chars"];
const LIST_ARGS = ["truthy"];
const JSON_ARGS = ["map", "value", "default"];

const argText = (arg, value) => {
    if (value === undefined || value === null) return "";
    if (LIST_ARGS.includes(arg) && Array.isArray(value)) return value.join(",");
    if (JSON_ARGS.includes(arg) && typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const parseArg = (arg, text) => {
    if (text === "") return undefined;
    if (NUMERIC_ARGS.includes(arg)) return Number(text);
    if (LIST_ARGS.includes(arg)) return text.split(",").map((v) => v.trim()).filter(Boolean);
    if (JSON_ARGS.includes(arg)) {
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    }
    return text;
};

export const TransformEditor = ({ transforms = [], onChange, catalogue = [] }) => {
    const [adding, setAdding] = useState("");
    const requiredArgs = Object.fromEntries(catalogue.map((t) => [t.op, t.args]));
    return (
        <div className="flex flex-wrap items-center gap-1">
            {transforms.map((transform, index) => {
                const args = [...(requiredArgs[transform.op] || []), ...(OPTIONAL_ARGS[transform.op] || [])];
                return (
                    <span key={index} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs">
                        <span className="font-mono">{transform.op}</span>
                        <InfoTip content={explain(GLOSSARY.transforms, transform.op)} size={12} />
                        {args.map((arg) => (
                            <input
                                key={arg}
                                title={arg}
                                placeholder={arg}
                                className="w-20 px-1 py-0.5 text-xs border border-gray-300 rounded bg-white font-mono"
                                value={argText(arg, transform[arg])}
                                onChange={(e) => onChange(transforms.map((t, i) => (i === index ? { ...t, [arg]: parseArg(arg, e.target.value) } : t)))}
                            />
                        ))}
                        <button type="button" title="Remove transform" onClick={() => onChange(transforms.filter((_, i) => i !== index))}>
                            <X size={12} />
                        </button>
                    </span>
                );
            })}
            <select
                className="text-xs border border-dashed border-gray-300 rounded-full px-2 py-0.5 bg-white text-gray-500"
                value={adding}
                onChange={(e) => {
                    if (e.target.value) onChange([...transforms, { op: e.target.value }]);
                    setAdding("");
                }}
            >
                <option value="">+ transform</option>
                {catalogue.map((t) => (
                    <option key={t.op} value={t.op}>{t.op}</option>
                ))}
            </select>
            <InfoTip content={TRANSFORMS_TIP} wide size={12} />
        </div>
    );
};

const MODES = [
    { value: "path", label: "Path" },
    { value: "const", label: "Fixed value" },
    { value: "template", label: "Template" },
    { value: "ref", label: "Context" },
    { value: "all", label: "All true" },
    { value: "any", label: "Any true" },
    { value: "advanced", label: "Advanced (JSON)" },
];

const CONTEXT_REFS = ["$key", "$index", "$product_key", "$source.repeat", "$source.each", "$source.page", "$source.url"];

export const expressionMode = (value) => {
    if (value === undefined || value === null || value === "" || typeof value === "string") return "path";
    if (value.path !== undefined) return "path";
    for (const mode of ["const", "template", "ref", "all", "any"]) {
        if (value[mode] !== undefined) return mode;
    }
    return "advanced";
};

const toObject = (value) => (typeof value === "string" ? { path: value } : value || {});

/**
 * Editor for one field expression.
 * Lookups, other-source lookups and case expressions use the JSON mode.
 */
const ExpressionEditor = ({ value, onChange, catalogue, onPick, pickActive }) => {
    const mode = expressionMode(value);
    const expr = toObject(value);
    const transforms = expr.transforms || [];

    const emit = (next) => {
        const withTransforms = transforms.length ? { ...next, transforms } : next;
        // Plain paths without transforms stay as strings, like hand-written configs.
        if (Object.keys(withTransforms).length === 1 && typeof withTransforms.path === "string") return onChange(withTransforms.path || undefined);
        onChange(withTransforms);
    };

    const setTransforms = (next) => {
        const rest = { ...expr };
        delete rest.transforms;
        if (next.length) return onChange({ ...rest, transforms: next });
        onChange(Object.keys(rest).length === 1 && typeof rest.path === "string" ? rest.path : rest);
    };

    const changeMode = (nextMode) => {
        const base = {
            path: { path: "" },
            const: { const: "" },
            template: { template: "" },
            ref: { ref: "$key" },
            all: { all: [""] },
            any: { any: [""] },
            advanced: { lookup: { in: "$root.items[*]", match: { id: "$variant.id" }, get: "value" } },
        }[nextMode];
        emit(base);
    };

    const paths = Array.isArray(expr.path) ? expr.path : [expr.path ?? ""];
    const list = expr.all || expr.any || [];

    return (
        <div className="flex flex-col gap-1 min-w-[260px]">
            <div className="flex items-start gap-1">
                <SelectInput className="max-w-[120px] !py-1 text-xs" value={mode} options={MODES} onChange={changeMode} />
                <div className="flex-1 flex flex-col gap-1">
                    {mode === "path" &&
                        paths.map((path, i) => (
                            <div key={i} className="flex items-center gap-1">
                                {i > 0 && <span className="text-xs text-gray-400">or</span>}
                                <TextInput
                                    monospace
                                    className="!py-1 text-xs"
                                    value={path}
                                    placeholder={i === 0 ? "e.g. title or $product.brand" : "fallback path"}
                                    onChange={(v) => {
                                        const next = paths.map((p, j) => (j === i ? v : p));
                                        emit({ path: next.length === 1 ? next[0] : next });
                                    }}
                                />
                                {i > 0 && (
                                    <IconButton title="Remove fallback" onClick={() => emit({ path: paths.filter((_, j) => j !== i).length === 1 ? paths[0] : paths.filter((_, j) => j !== i) })}>
                                        <X size={14} />
                                    </IconButton>
                                )}
                            </div>
                        ))}
                    {mode === "const" && <TextInput className="!py-1 text-xs" value={argText("value", expr.const)} onChange={(v) => emit({ const: parseArg("value", v) })} />}
                    {mode === "template" && <TextInput monospace className="!py-1 text-xs" value={expr.template} placeholder="{{$product.brand}} {{color}}" onChange={(v) => emit({ template: v })} />}
                    {mode === "ref" && (
                        <div className="flex items-center gap-1">
                            <SelectInput className="!py-1 text-xs" value={expr.ref} options={CONTEXT_REFS} onChange={(v) => emit({ ref: v })} />
                            <InfoTip content={REFS_TIP} wide />
                        </div>
                    )}
                    {(mode === "all" || mode === "any") &&
                        list.map((item, i) => (
                            <div key={i} className="flex items-center gap-1">
                                {i > 0 && <span className="text-xs text-gray-400">{mode === "all" ? "and" : "or"}</span>}
                                <TextInput
                                    monospace
                                    className="!py-1 text-xs"
                                    value={typeof item === "string" ? item : JSON.stringify(item)}
                                    onChange={(v) => emit({ [mode]: list.map((p, j) => (j === i ? v : p)) })}
                                />
                                <IconButton title="Remove" disabled={list.length === 1} onClick={() => emit({ [mode]: list.filter((_, j) => j !== i) })}>
                                    <X size={14} />
                                </IconButton>
                            </div>
                        ))}
                    {mode === "advanced" && <JsonEditor rows={4} value={value} onChange={onChange} />}
                </div>
                {mode === "path" && onPick && (
                    <IconButton title="Pick from sample" className={pickActive ? "text-primary bg-blue-50" : ""} onClick={onPick}>
                        <MousePointerClick size={16} />
                    </IconButton>
                )}
                {mode === "path" && (
                    <IconButton title="Add fallback path" onClick={() => emit({ path: [...paths, ""] })}>
                        <Plus size={14} />
                    </IconButton>
                )}
                {(mode === "all" || mode === "any") && (
                    <IconButton title="Add" onClick={() => emit({ [mode]: [...list, ""] })}>
                        <Plus size={14} />
                    </IconButton>
                )}
            </div>
            {mode !== "advanced" && value !== undefined && value !== "" && (
                <TransformEditor catalogue={catalogue} transforms={transforms} onChange={setTransforms} />
            )}
        </div>
    );
};

export default ExpressionEditor;
